import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Award, LayoutDashboard, LayoutGrid, Map as MapIcon, BarChart3,
  ShieldCheck, BookOpen, Settings, Activity, SlidersHorizontal,
  ClipboardCheck, Zap, MoreHorizontal,
  User, Shield, LogOut, HelpCircle, MessageSquareWarning,
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { ROLES, ROLE_LABELS } from '../constants/roles';
import { useDemoMode } from '../context/DemoModeContext';
import { useHelp } from '../context/HelpContext';
import { useProductTour } from '../context/ProductTourContext';
import useDisclosureFrameworks from '../hooks/useDisclosureFrameworks';

/**
 * Horizontal dash-topnav (handoffs/01-design-pass.md §6).
 *
 * Renders ALL tabs for ALL roles — the role never hides a tab; it only gates
 * the depth of content inside each view (specced view-by-view as those views
 * are built). This component is the nav shell + the role chip; it does not
 * build the not-yet-existing views.
 *
 * The app's Map/Disclosure/Reports are per-property, so those tabs are
 * enabled only when a property is in route context (/map/:id,
 * /disclosure/:id, /reports/:id); otherwise they're disabled with a hint. Tabs
 * whose views do not exist yet (Operations) are shown disabled with a
 * "Coming soon" hint so the full RB structure is visible without leading
 * anywhere broken.
 *
 * Analytics lives behind the ⋯ overflow after Operations while placements
 * are unsettled (Marcus 2026-07-15): reachable in prod for per-field deep
 * links, labelled "Features In Development", not a first-class primary tab.
 */

// Pull a farmId out of the current path when one is in context.
function farmIdFromPath(pathname) {
  const m = /^\/(?:overview|assessments|diagnostics|actions|map|analytics|reports|disclosure|setup|credentials)\/([^/]+)/.exec(pathname || '');
  return m ? decodeURIComponent(m[1]) : null;
}

function activeKey(pathname) {
  if (/^\/dashboard/.test(pathname)) return 'dashboard';
  if (/^\/overview\//.test(pathname)) return 'overview';
  if (/^\/assessments\//.test(pathname)) return 'assessments';
  if (/^\/diagnostics\//.test(pathname)) return 'diagnostics';
  if (/^\/actions\//.test(pathname)) return 'actions';
  if (/^\/map\//.test(pathname)) return 'map';
  if (/^\/analytics\//.test(pathname)) return 'analytics';
  if (/^\/disclosure\//.test(pathname)) return 'disclosure';
  if (/^\/reports\//.test(pathname)) return 'reports';
  if (/^\/credentials\//.test(pathname)) return 'credentials';
  if (/^\/setup\//.test(pathname)) return 'setup';
  return null;
}

function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function DashTopNav({ role, user, onOpenFeedback }) {
  const navigate = useNavigate();
  const location = useLocation();
  const demoMode = useDemoMode();
  const { openHelp } = useHelp();
  const { openHub } = useProductTour();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef(null);

  const farmId = farmIdFromPath(location.pathname);
  const { needsTabMarker } = useDisclosureFrameworks(farmId, null);
  const active = activeKey(location.pathname);
  const analyticsActive = active === 'analytics';
  const overflowEnabled = !!farmId;

  useEffect(() => {
    if (!overflowOpen) return undefined;
    const onDocClick = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [overflowOpen]);

  // Close overflow when the route changes (e.g. after choosing Analytics).
  useEffect(() => {
    setOverflowOpen(false);
  }, [location.pathname]);

  // RB-UX order (handoff 22): Overview → Assessments → Diagnostics → Actions → Map → …
  // Analytics is intentionally omitted from primary tabs (overflow after Operations).
  const tabs = [
    { key: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard, to: '/dashboard', enabled: true },
    { key: 'overview', label: 'Overview', Icon: LayoutGrid, to: farmId ? `/overview/${farmId}` : null, enabled: !!farmId, needProperty: true },
    { key: 'assessments', label: 'Assessments', Icon: ClipboardCheck, to: farmId ? `/assessments/${farmId}` : null, enabled: !!farmId, needProperty: true, isNew: true },
    { key: 'diagnostics', label: 'Diagnostics', Icon: Activity, to: farmId ? `/diagnostics/${farmId}` : null, enabled: !!farmId, needProperty: true, isNew: true },
    { key: 'actions', label: 'Actions', Icon: Zap, to: farmId ? `/actions/${farmId}` : null, enabled: !!farmId, needProperty: true, isNew: true },
    { key: 'map', label: 'Map', Icon: MapIcon, to: farmId ? `/map/${farmId}` : null, enabled: !!farmId, needProperty: true },
    { key: 'disclosure', label: 'Disclosure', Icon: ShieldCheck, to: farmId ? `/disclosure/${farmId}` : null, enabled: !!farmId, needProperty: true },
    { key: 'reports', label: 'Reports', Icon: BookOpen, to: farmId ? `/reports/${farmId}` : null, enabled: !!farmId, needProperty: true },
    { key: 'credentials', label: 'Credentials', Icon: Award, to: farmId ? `/credentials/${farmId}` : null, enabled: !!farmId, needProperty: true },
    { key: 'setup', label: 'Setup', Icon: Settings, to: farmId ? `/setup/${farmId}` : null, enabled: !!farmId, needProperty: true },
    { key: 'operations', label: 'Operations', Icon: SlidersHorizontal, enabled: false, soon: true },
  ];

  // Role chip: name + role, with "Demo" surfaced when viewing in demo mode.
  const displayName = user?.displayName || user?.email || 'Account';
  const inDemo = !!demoMode?.isDemoModeEnabled;
  const roleLabel = inDemo ? 'Demo' : (ROLE_LABELS[role] || ROLE_LABELS[ROLES.USER] || 'User');

  // Same sign-out as Layout.handleLogout, so both surfaces behave identically.
  const handleSignOut = async () => { await signOut(auth); navigate('/login'); };

  const tabBase =
    'relative flex items-center gap-1.5 px-2.5 py-3 text-[13px] font-medium whitespace-nowrap border-b-2 transition-colors';

  return (
    <nav
      className="relative z-30 shrink-0 bg-white border-b border-slate-200 flex items-center gap-1 px-2 sm:px-4"
      aria-label="Primary"
    >
      {/* Scroll primary tabs separately from the overflow ⋯ so the dropdown is
          never clipped by overflow-x-auto and ⋯ stays visible after Operations. */}
      <div className="flex items-stretch flex-1 min-w-0">
        <div className="flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto">
          {tabs.map(({ key, label, Icon, to, enabled, soon, needProperty, isNew }) => {
            const isActive = active === key;
            const hint = soon
              ? 'Coming soon'
              : (!enabled && needProperty ? 'Open a property to view this' : undefined);
            const stateClass = isActive
              ? 'border-eddp-teal text-eddp-primary'
              : enabled
                ? 'border-transparent text-slate-600 hover:text-eddp-primary hover:border-slate-200'
                : 'border-transparent text-slate-300 cursor-not-allowed';
            return (
              <button
                key={key}
                type="button"
                disabled={!enabled}
                aria-current={isActive ? 'page' : undefined}
                title={hint}
                onClick={() => { if (enabled && to) navigate(to); }}
                data-tour={`nav-${key}`}
                className={`${tabBase} ${stateClass}`}
              >
                <Icon size={15} className="shrink-0" />
                <span>{label}</span>
                {isNew && (
                  <span className="ml-0.5 text-[8px] font-bold uppercase tracking-wide bg-eddp-teal/15 text-eddp-teal px-1 py-px rounded">
                    New
                  </span>
                )}
                {key === 'disclosure' && needsTabMarker && (
                  <span
                    className="ml-0.5 w-1.5 h-1.5 rounded-full bg-eddp-teal shrink-0"
                    title="Choose disclosure frameworks"
                    aria-label="Choose disclosure frameworks"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Overflow after Operations: park WIP surfaces (Analytics) without a
            first-class primary tab. Routes / deep links stay intact. */}
        <div className="relative shrink-0" ref={overflowRef}>
          <button
            type="button"
            disabled={!overflowEnabled}
            aria-label="More navigation"
            aria-haspopup="menu"
            aria-expanded={overflowOpen}
            aria-current={analyticsActive ? 'page' : undefined}
            title={overflowEnabled ? 'More' : 'Open a property to view this'}
            onClick={() => { if (overflowEnabled) setOverflowOpen((o) => !o); }}
            className={`${tabBase} ${
              analyticsActive || overflowOpen
                ? 'border-eddp-teal text-eddp-primary'
                : overflowEnabled
                  ? 'border-transparent text-slate-600 hover:text-eddp-primary hover:border-slate-200'
                  : 'border-transparent text-slate-300 cursor-not-allowed'
            }`}
          >
            <MoreHorizontal size={15} className="shrink-0" />
          </button>
          {overflowOpen && overflowEnabled && (
            <div
              role="menu"
              className="absolute right-0 top-full mt-0 z-50 min-w-[16rem] bg-white border border-slate-200 rounded-xl shadow-lg py-1"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  navigate(`/analytics/${farmId}`);
                  setOverflowOpen(false);
                }}
                className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-left transition-colors ${
                  analyticsActive
                    ? 'bg-slate-50 text-eddp-primary'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-eddp-primary'
                }`}
              >
                <BarChart3 size={16} className="shrink-0 mt-0.5 text-slate-400" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium leading-snug">
                    Analytics - Features In Development
                  </span>
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Help & Glossary "?" — shared-chrome fixture on EVERY page (handoff 12
          §A). Opens the single app-wide HelpDrawer mounted in Layout via the
          HelpContext. Sits immediately left of the account block. */}
      <button
        type="button"
        data-tour="nav-help"
        onClick={openHelp}
        aria-label="Help and glossary"
        title="Help & glossary"
        className="shrink-0 h-8 w-8 flex items-center justify-center rounded-full text-slate-500 hover:text-eddp-primary hover:bg-slate-100 transition-colors"
      >
        <HelpCircle size={18} />
      </button>

      {/* Account chip + hover/focus menu (mirrors the sidebar footer) */}
      <div className="shrink-0 flex items-center gap-2 pl-2 pr-1 relative group" tabIndex={0}>
        <div className="hidden sm:flex flex-col items-end leading-tight cursor-pointer">
          <span className="text-[12px] font-semibold text-slate-700 max-w-[12rem] truncate">{displayName}</span>
          <span className="text-[10px] text-slate-400">{roleLabel}</span>
        </div>
        <div
          className="h-8 w-8 rounded-full bg-eddp-primary text-eddp-cream flex items-center justify-center text-[11px] font-bold cursor-pointer"
          title={`${displayName} \u00b7 ${roleLabel}`}
        >
          {initialsOf(displayName)}
        </div>

        {/* pt-2 bridges the gap so moving onto the card keeps the group hovered.
            group-focus-within keeps it keyboard-reachable. */}
        <div className="hidden group-hover:block group-focus-within:block absolute right-0 top-full pt-2 w-56 z-50">
          <div className="bg-white rounded-xl border border-slate-200 shadow-lg py-1 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-100">
              <div className="text-sm font-semibold text-eddp-dark truncate">{displayName}</div>
              <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
            </div>
            <button type="button" onClick={() => navigate('/dashboard')}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-eddp-primary text-left">
              <LayoutDashboard size={16} className="shrink-0 text-slate-400" /> Dashboard
            </button>
            <button type="button" onClick={() => navigate('/profile')}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-eddp-primary text-left">
              <User size={16} className="shrink-0 text-slate-400" /> Profile
            </button>
            {role === ROLES.ADMIN && (
              <button type="button" onClick={() => navigate('/admin')}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-eddp-primary text-left">
                <Shield size={16} className="shrink-0 text-slate-400" /> Admin Console
              </button>
            )}
            {/* Feedback & bug report (handoff 14): opens the shared capture modal
                mounted in Layout (global chrome, like the Help drawer). */}
            <button type="button" onClick={() => onOpenFeedback?.()}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-eddp-primary text-left">
              <MessageSquareWarning size={16} className="shrink-0 text-slate-400" /> Feedback &amp; bug report
            </button>
            <button type="button" onClick={() => openHub()}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-eddp-primary text-left">
              <HelpCircle size={16} className="shrink-0 text-slate-400" /> Take a tour
            </button>
            <div className="border-t border-slate-100 my-1" />
            <button type="button" onClick={handleSignOut}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-red-600 text-left">
              <LogOut size={16} className="shrink-0 text-slate-400" /> Sign Out
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
